import { Modal, Button, Spinner, Tooltip, ConfirmDialog } from '@scale/scaleui';
import React, { useState, useMemo, useEffect } from 'react';
import EmailTemplateSelection from './EmailTemplateSelection';
import EmailEditor from './EmailEditor';
import {
  EmailTemplateData,
  PAY_COMMS_WARN_KEYWORDS,
  EmailCommsTemplateStatus,
  EmailGuardrailSuggestion,
} from '@scale/shared-types';
import { Alert } from 'design-system';
import PayWarning from './PayWarning';
import { DripsHook } from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/useDrips';
import { useQuery, useMutation } from 'react-query';
import {
  getEmailCommsTemplates,
  validateEmailContent,
  getEmailCommsTemplateVersion,
} from 'react-scale/corp/lib/api/growth';
import SAlert from 'react-s-alert';

export enum EmailEditorTabOptions {
  TemplateVariables = 'Template Variables',
  EmailContent = 'Email Content',
}

export enum EmailPreviewTabOptions {
  PreviewContent = 'Preview',
  Suggestions = 'Suggestions',
}

interface IEmailTemplateModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  csvHeaders?: string[];
  dripsHook: DripsHook;
  index: number;
  userId?: string;
  isViewOnly?: boolean;
}

enum EmailTemplateModalSteps {
  Select = 'select',
  Edit = 'edit',
}
const EmailTemplateModalV2: React.FC<IEmailTemplateModalV2Props> = ({
  isOpen,
  onClose,
  csvHeaders,
  dripsHook,
  index,
  userId,
  isViewOnly = false,
}) => {
  const { isFetching: isLoadingExistingTemplate, data: existingTemplate } = useQuery(
    ['get-email-comms-template-version'],
    async () => {
      const drip = dripsHook.drips[index];
      if (!drip?.templateId) {
        return null; // If no templateId, return null and go to selection
      }
      return getEmailCommsTemplateVersion(drip?.templateId, drip?.templateVersion ?? 0);
    },
    {
      enabled: isOpen,
      refetchOnWindowFocus: false,
    },
  );

  const { mutateAsync: validateEmailContentMutate, isLoading: isLoadingValidateEmailContent } =
    useMutation(
      'validate-email-content',
      ({ body, subject }: { body: string; subject: string }) => validateEmailContent(body, subject),
      {
        retry: false,
      },
    );

  const [step, setStep] = useState(EmailTemplateModalSteps.Select);

  const { isFetching: isLoading, data: rawTemplates } = useQuery(
    'get-email-comms-templates',
    () =>
      getEmailCommsTemplates({
        status: EmailCommsTemplateStatus.Public,
        latest: true,
      }),
    {
      enabled: step === EmailTemplateModalSteps.Select,
      refetchOnWindowFocus: false,
    },
  );

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateData | undefined>();

  // store selected template before user actually switches/selects
  const [tempSelectedTemplate, setTempSelectedTemplate] = useState<EmailTemplateData | undefined>();

  // outside of EmailEditor component b/c onSave will need to be managed externally
  const [senderName, setSenderName] = useState<string>(
    dripsHook.drips[index]?.email?.fromName ?? '',
  );
  const [senderEmail, setSenderEmail] = useState<string>(
    dripsHook.drips[index]?.email?.fromAddress ?? '',
  );
  const [subject, setSubject] = useState<string>(dripsHook.drips[index]?.email?.subject ?? '');
  const [body, setBody] = useState<string>(dripsHook.drips[index]?.email?.body ?? '');
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    dripsHook.drips[index]?.variableValues ?? {},
  );

  const [showPayWarning, setShowPayWarning] = useState(false);
  const [showSwitchTemplateWarning, setShowSwitchTemplateWarning] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [validationSuggestions, setValidationSuggestions] = useState<
    EmailGuardrailSuggestion[] | undefined
  >(undefined);

  // ql-indent is a class that is added by the rich text editor when a <li> or <ul> is nested
  // but it wont display correctly in the email rendering
  const showNestedListWarning = useMemo(() => body.toLowerCase().includes('ql-indent'), [body]);

  const availableVariables = useMemo(
    () => (csvHeaders && csvHeaders.length > 0 ? csvHeaders : ['email', 'first_name', 'last_name']),
    [csvHeaders],
  );

  // static difference of template's variables - available
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) {
      return [];
    }

    return selectedTemplate.variables.filter(
      (variable: string) => !availableVariables.includes(variable),
    );
  }, [selectedTemplate, availableVariables]);

  // show preview tab when modal is opened
  useEffect(() => {
    if (isOpen) {
      setPreviewTab(EmailPreviewTabOptions.PreviewContent);
    }
  }, [isOpen]);

  // filter out old variableValues when templateVariables change
  useEffect(() => {
    const newVariableValues: Record<string, string> = {};
    templateVariables.forEach(variable => {
      if (variableValues[variable]) {
        newVariableValues[variable] = variableValues[variable];
      }
    });
    setVariableValues({ ...variableValues, ...newVariableValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateVariables]);

  const emailEditorTabs = useMemo(() => {
    return [
      ...(templateVariables.length === 0
        ? []
        : [
            {
              id: EmailEditorTabOptions.TemplateVariables,
              label: EmailEditorTabOptions.TemplateVariables,
            },
          ]),
      { id: EmailEditorTabOptions.EmailContent, label: EmailEditorTabOptions.EmailContent },
    ];
  }, [templateVariables]);

  const emailPreviewTabs = [
    { id: EmailPreviewTabOptions.PreviewContent, label: EmailPreviewTabOptions.PreviewContent },
    { id: EmailPreviewTabOptions.Suggestions, label: EmailPreviewTabOptions.Suggestions },
  ];
  const [previewTab, setPreviewTab] = useState(emailPreviewTabs[0].id);

  // checks that all the template variables that appear in body/subject are defined
  const hasVariablesFilled = useMemo(() => {
    return templateVariables.reduce((acc, variable) => {
      const variableInEmail =
        body.includes(`{{${variable}}}`) || subject.includes(`{{${variable}}}`);
      return acc && (!!variableValues[variable] || !variableInEmail);
    }, true);
  }, [variableValues, templateVariables, body, subject]);

  const saveDisabled = useMemo(
    () =>
      !subject ||
      !senderName ||
      !isValidEmail ||
      isViewOnly ||
      !hasVariablesFilled ||
      isLoadingValidateEmailContent,
    [
      isValidEmail,
      senderName,
      subject,
      isViewOnly,
      hasVariablesFilled,
      isLoadingValidateEmailContent,
    ],
  );

  useEffect(() => {
    if (!isLoadingExistingTemplate && existingTemplate) {
      if (dripsHook.drips[index]?.email) {
        setSenderEmail(dripsHook.drips[index]?.email!.fromAddress ?? '');
        setSenderName(dripsHook.drips[index]?.email!.fromName ?? '');
        setSubject(dripsHook.drips[index]?.email!.subject ?? '');
        setBody(dripsHook.drips[index]?.email!.body ?? '');
      }

      setValidationSuggestions(dripsHook.drips[index]?.bypassedValidations);
      setSelectedTemplate(existingTemplate);
      setTempSelectedTemplate(existingTemplate);
      setShowSwitchTemplateWarning(!!existingTemplate);
      setStep(EmailTemplateModalSteps.Edit);
    } else if (!existingTemplate) {
      setStep(EmailTemplateModalSteps.Select);
      setPreviewTab(EmailPreviewTabOptions.PreviewContent);
      setSelectedTemplate(undefined);
      setTempSelectedTemplate(undefined);
      setShowSwitchTemplateWarning(false);
      setValidationSuggestions(undefined);
    }
  }, [existingTemplate, isLoadingExistingTemplate, index, dripsHook.drips]);

  const onTemplateSelect = () => {
    setVariableValues({});
    setSelectedTemplate(tempSelectedTemplate);
    setSenderName(tempSelectedTemplate!.fromName);
    setSenderEmail(tempSelectedTemplate!.fromAddr);
    setSubject(tempSelectedTemplate!.subject);
    setBody(tempSelectedTemplate!.body);
    setStep(EmailTemplateModalSteps.Edit);
    setShowSwitchTemplateWarning(true);
  };

  const onSaveEdit = () => {
    const newDrips = dripsHook.drips.map((drip, i) => {
      if (i === index) {
        return {
          ...drip,
          templateId: selectedTemplate!.templateId,
          templateVersion: selectedTemplate!.version,
          variableValues,
          email: {
            fromAddress: senderEmail,
            fromName: senderName,
            subject,
            body,
            sendgridTemplateId: selectedTemplate!.sendgridTemplateId, // for sending test email
          },
          name: selectedTemplate!.name,
          tested: false,
          bypassedValidations: validationSuggestions,
        };
      }
      return drip;
    });
    dripsHook.setDrips(newDrips);

    setShowPayWarning(false);
    onClose();
  };

  const onSaveEditAttempt = async (shouldValidateContent = true) => {
    const containsKeywords = PAY_COMMS_WARN_KEYWORDS.some(
      keyword =>
        body.toLowerCase().includes(keyword.toLowerCase()) ||
        subject.toLowerCase().includes(keyword.toLowerCase()),
    );

    // Only run quality guardrails if the email body has changed from the original template
    if (selectedTemplate?.body !== body && shouldValidateContent) {
      try {
        const validationResponse = await validateEmailContentMutate({ body, subject });
        if (validationResponse.success) {
          setValidationSuggestions([]);
          SAlert.success('Successfully validated email content');
        } else {
          setValidationSuggestions(validationResponse.suggestions);
          setPreviewTab(EmailPreviewTabOptions.Suggestions);
          return;
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          SAlert.error('Your session has expired. Please log in again to access this tool.');
        } else {
          SAlert.error('Error validating email content. Please try again.', e);
        }
      }
    }

    if (containsKeywords) {
      setShowPayWarning(true);
    } else {
      onSaveEdit();
    }
  };

  const onChangeTemplate = () => {
    setStep(EmailTemplateModalSteps.Select);
  };

  const onBack = () => {
    setTempSelectedTemplate(selectedTemplate);
    setStep(EmailTemplateModalSteps.Edit);
  };

  return (
    <>
      <Modal
        innerClassName={'w-4/5 h-full'}
        open={isOpen}
        onClose={() => setCancelConfirmationOpen(true)}
        title={
          isViewOnly
            ? 'View Email'
            : step === EmailTemplateModalSteps.Select
            ? 'Select a template'
            : 'Edit email'
        }
        actions={
          <div className="flex flex-row-reverse justify-between items-center">
            {step === EmailTemplateModalSteps.Select ? (
              <div className="flex gap-3">
                {showSwitchTemplateWarning && (
                  <Button size="lg" variant="neutral" key="back" onClick={onBack}>
                    Back
                  </Button>
                )}
                <Button
                  size="lg"
                  key="confirm"
                  onClick={onTemplateSelect}
                  disabled={!tempSelectedTemplate}
                >
                  {showSwitchTemplateWarning ? 'Change' : 'Select'}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  size="lg"
                  variant="white"
                  key="cancel"
                  onClick={() => setCancelConfirmationOpen(true)}
                  className="border-none shadow-none"
                >
                  Cancel
                </Button>
                {validationSuggestions && (
                  <Button
                    size="lg"
                    variant="white"
                    key="save_anyway"
                    onClick={() => onSaveEditAttempt(false)}
                  >
                    Save anyway
                  </Button>
                )}
                <Tooltip
                  asChild
                  side="left"
                  target={
                    <Button
                      size="lg"
                      key="validate"
                      onClick={() => onSaveEditAttempt(true)}
                      disabled={!!saveDisabled}
                    >
                      Validate
                    </Button>
                  }
                  text={
                    hasVariablesFilled ? '' : 'Template variables have not been completely filled.'
                  }
                />
              </div>
            )}

            {step === EmailTemplateModalSteps.Edit && showNestedListWarning && (
              <Alert severity="warning" className="py-0">
                Please avoid using lists within lists (nested bullet points), as they may not
                display correctly in the email.
              </Alert>
            )}
          </div>
        }
      >
        {isLoading || isLoadingExistingTemplate ? (
          <div className="flex h-full w-full justify-center items-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {step === EmailTemplateModalSteps.Select || !selectedTemplate ? (
              <EmailTemplateSelection
                selectedTemplate={tempSelectedTemplate}
                setSelectedTemplate={setTempSelectedTemplate}
                rawTemplates={rawTemplates ?? []}
                showSwitchTemplateWarning={showSwitchTemplateWarning}
              />
            ) : (
              <EmailEditor
                userId={userId}
                selectedTemplate={selectedTemplate!}
                availableVariables={availableVariables}
                templateVariables={templateVariables}
                body={body}
                subject={subject}
                senderName={senderName}
                senderEmail={senderEmail}
                variableValues={variableValues}
                setBody={setBody}
                setSubject={setSubject}
                setSenderName={setSenderName}
                setSenderEmail={setSenderEmail}
                setVariableValues={setVariableValues}
                onChangeTemplate={onChangeTemplate}
                isValidEmail={isValidEmail}
                setIsValidEmail={setIsValidEmail}
                isViewOnly={isViewOnly}
                emailEditorTabs={emailEditorTabs}
                emailPreviewTabs={emailPreviewTabs}
                previewTab={previewTab}
                setPreviewTab={setPreviewTab}
                validationSuggestions={validationSuggestions}
                isLoadingEmailContentValidation={isLoadingValidateEmailContent}
              />
            )}
            <PayWarning
              showPayWarning={showPayWarning}
              setShowPayWarning={setShowPayWarning}
              onSave={onSaveEdit}
            />
          </>
        )}
      </Modal>
      <ConfirmDialog
        open={cancelConfirmationOpen}
        className={'z-50'}
        onConfirm={() => {
          setCancelConfirmationOpen(false);
          setValidationSuggestions([]);
          (onClose ?? (() => {}))();
        }}
        onCancel={() => setCancelConfirmationOpen(false)}
      >
        Are you sure you want to exit? Any information entered on this page will be lost.
      </ConfirmDialog>
    </>
  );
};

export default EmailTemplateModalV2;
